import React from 'react';
import { SlidersHorizontal, ClipboardCheck, Link2 } from 'lucide-react';
import { StatusBadge } from 'src/components/common/StatusBadge';

const fieldBase =
  'w-full px-3 py-[9px] rounded-lg border border-input text-[13px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none disabled:opacity-50 disabled:cursor-not-allowed';

const SectionCard = ({ icon: Icon, title, subtitle, badge, children }) => (
  <div className="rounded-[10px] border border-border bg-card overflow-hidden">
    <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-border">
      <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-[15px] h-[15px]" strokeWidth={1.9} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-[650] text-foreground-strong leading-tight">
          {title}
        </div>
        {subtitle && (
          <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      {badge}
    </div>
    <div className="p-[18px]">{children}</div>
  </div>
);

const FieldLabel = ({ children }) => (
  <div className="block text-[12.5px] font-[600] text-foreground mb-1.5">
    {children}
  </div>
);

export const TestGroupPreviewDisplay = ({ groupData }) => {
  if (!groupData) {
    return (
      <div className="max-w-[760px] mx-auto px-8">
        <div className="rounded-[10px] border border-border bg-card p-8 text-center">
          <p className="text-[13px] text-muted-foreground">
            No group data available
          </p>
        </div>
      </div>
    );
  }

  const tests = groupData.tests || [];
  const vendorLabel =
    groupData.vendor_name ||
    (groupData.vendor_id ? `Vendor #${groupData.vendor_id}` : '—');

  return (
    <div className="max-w-[760px] mx-auto space-y-[18px] px-8 pb-20">
      {/* ── Group Details ── */}
      <SectionCard
        icon={SlidersHorizontal}
        title="Group details"
        subtitle={
          groupData.id ? (
            <span className="font-mono tracking-[.02em]">#{groupData.id}</span>
          ) : (
            'Name and vendor for this test group'
          )
        }
      >
        {/* Name */}
        <div className="mb-4">
          <FieldLabel>Group name</FieldLabel>
          <input
            className={fieldBase}
            value={groupData.name || ''}
            disabled
            readOnly
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <FieldLabel>Description</FieldLabel>
          <textarea
            className={`${fieldBase} min-h-[80px] resize-none leading-[1.55]`}
            value={groupData.description || ''}
            disabled
            readOnly
          />
        </div>

        {/* Vendor + Status */}
        <div className="grid grid-cols-2 gap-[14px]">
          <div>
            <FieldLabel>Vendor</FieldLabel>
            <input
              className={fieldBase}
              value={vendorLabel}
              disabled
              readOnly
            />
          </div>
          <div>
            <FieldLabel>Status</FieldLabel>
            <div className="pt-2">
              <StatusBadge value={groupData.status || 'ACTIVE'} />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Tests ── */}
      <SectionCard
        icon={ClipboardCheck}
        title="Tests"
        subtitle={`${tests.length} test${tests.length !== 1 ? 's' : ''} in this group`}
        badge={
          <span className="font-mono text-[11.5px] font-semibold text-primary bg-brand-bg-subtle px-2.5 py-0.5 rounded-[6px] flex-shrink-0">
            {tests.length} selected
          </span>
        }
      >
        {tests.length === 0 ? (
          <div className="border-2 border-dashed border-input rounded-[10px] p-10 text-center">
            <p className="text-[14px] font-semibold text-foreground-strong mb-1">
              No tests in this group
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-[18px] -mb-[18px]">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-[18px] py-[13px] text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card border-b border-border w-[38%]">
                    Test Name
                  </th>
                  <th className="text-left px-4 py-[13px] text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card border-b border-border w-[28%]">
                    Description
                  </th>
                  <th className="text-center px-4 py-[13px] text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card border-b border-border w-[12%]">
                    Max Score
                  </th>
                  <th className="text-center px-4 py-[13px] text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card border-b border-border w-[12%]">
                    Duration
                  </th>
                  <th className="text-center px-[18px] py-[13px] text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card border-b border-border w-[14%]">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {tests.map((test, index) => (
                  <tr
                    key={test.id || index}
                    className="border-b border-border hover:bg-brand-bg-subtle transition-colors"
                  >
                    <td className="px-[18px] py-3 align-middle">
                      <div className="flex items-center gap-2.5">
                        <div className="w-[22px] h-[22px] rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center text-[10.5px] font-mono font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-[13px] font-[550] text-foreground-strong">
                            {test.name || test.title || 'Untitled Test'}
                          </div>
                          <div className="text-[11px] text-primary font-mono tracking-[.02em] mt-0.5">
                            {test.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <span className="text-[12.5px] text-muted-foreground line-clamp-2">
                        {test.description || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className="inline-block px-2 py-0.5 rounded bg-muted text-muted-foreground text-[12.5px] font-medium tabular-nums">
                        {test.max_score ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className="text-[12.5px] text-muted-foreground tabular-nums">
                        {test.duration_minutes != null
                          ? `${test.duration_minutes} min`
                          : '—'}
                      </span>
                    </td>
                    <td className="px-[18px] py-3 text-center align-middle">
                      <StatusBadge value={test.status || 'ACTIVE'} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/30">
                  <td
                    colSpan="2"
                    className="px-[18px] py-3 text-right text-[12.5px] font-semibold text-muted-foreground"
                  >
                    Total tests
                  </td>
                  <td
                    colSpan="3"
                    className="px-4 py-3 text-left text-[13px] font-bold text-foreground-strong tabular-nums"
                  >
                    {tests.length}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>

      {/* ── External References ── */}
      {tests.length > 0 && (
        <SectionCard
          icon={Link2}
          title="External test references"
          subtitle="Vendor-assigned IDs for each test"
        >
          <div className="space-y-2">
            {tests.map((test, index) => (
              <div key={test.id || index} className="flex items-center gap-2.5">
                <div className="w-[24px] h-[24px] rounded-[6px] bg-brand-bg-subtle text-primary font-mono text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </div>
                <span
                  className="text-[13px] font-[500] text-foreground w-[180px] flex-shrink-0 truncate"
                  title={test.name || test.title}
                >
                  {test.name || test.title}
                </span>
                <span
                  className={`flex-1 px-2.5 py-2 rounded-[7px] border font-mono text-[12px] bg-card ${test.external_test_ref ? 'border-input text-foreground-strong' : 'border-border text-muted-foreground'}`}
                >
                  {test.external_test_ref || '—'}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};
